import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_PROJECT_STATUSES } from '@lims/shared-types';
import { createProjectSchema, updateProjectSchema } from '@lims/shared-validation';
import { clientsApi, projectsApi, type ProjectDetail } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  DateField,
  ErrorState,
  FormPage,
  FormSection,
  LoadingState,
  SelectField,
  TextAreaField,
  TextField,
  useSaveErrors,
  useSaveMutation,
  validationErrors,
  useLoadOnce,
} from '../../components/ui/index.js';
import { optionalText } from '../clients/bilingual.js';

/**
 * Create or edit a project.
 *
 * EVERY TEXT IS ONE FIELD (D183): the name, the description and the three
 * parties are typed once, in whatever language the desk uses. An empty
 * optional field is sent as `null` — never as an empty object. The old form
 * sent `{ ar: null, en: null }` for every empty pair, the schema refused it,
 * and the page showed only three hand-picked errors, so a project saved
 * without a contractor simply did not save and said nothing. The shared save
 * pattern (`FormPage`, `useSaveMutation`) now shows every refusal.
 *
 * THE THREE PARTY FIELDS ARE OPTIONAL, and the form says so rather than
 * marking them required and letting the desk invent a consultant. A blank is
 * honest; a name typed to satisfy a form is not.
 *
 * THE REFERENCE IS THE CLIENT'S OWN NUMBER, not one the laboratory generates.
 *
 * A SUSPENDED CLIENT CANNOT TAKE A NEW PROJECT. The refusal comes from the
 * server, with a reason, and is shown against the client field.
 */

interface ProjectValues {
  clientId: string | null;
  name: string;
  reference: string;
  description: string;
  contractorName: string;
  consultantName: string;
  ownerName: string;
  startDate: string | null;
  expectedEndDate: string | null;
  status: string | null;
  notes: string;
  reason: string;
}

function valuesFrom(project: ProjectDetail): ProjectValues {
  return {
    clientId: project.clientId,
    name: project.name,
    reference: project.reference ?? '',
    description: project.description ?? '',
    contractorName: project.contractorName ?? '',
    consultantName: project.consultantName ?? '',
    ownerName: project.ownerName ?? '',
    startDate: project.startDate,
    expectedEndDate: project.expectedEndDate,
    status: project.status,
    notes: project.notes ?? '',
    reason: '',
  };
}

export function ProjectFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { can } = useAuth();

  const isNew = id === undefined;
  const canEdit = can(isNew ? PERMISSIONS.PROJECTS_CREATE : PERMISSIONS.PROJECTS_UPDATE);

  const [values, setValues] = useState<ProjectValues>({
    clientId: searchParams.get('clientId'),
    name: '',
    reference: '',
    description: '',
    contractorName: '',
    consultantName: '',
    ownerName: '',
    startDate: null,
    expectedEndDate: null,
    status: null,
    notes: '',
    reason: '',
  });
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const set = <Field extends keyof ProjectValues>(field: Field) =>
    (value: ProjectValues[Field]) => {
      setValues((current) => ({ ...current, [field]: value }));
      setLocalErrors((current) => {
        if (current[field] === undefined) return current;
        const { [field]: _removed, ...rest } = current;
        return rest;
      });
    };

  /**
   * Only ACTIVE clients are offered for a new project. A courtesy, not the
   * control: choosing a suspended one by any other means meets the refusal.
   */
  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
    enabled: isNew,
  });

  const project = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsApi.get(id as string),
    enabled: !isNew,
  });

  useLoadOnce(project.data, (record) => {
    setValues(valuesFrom(record));
  });

  /** The request body. Every empty optional text is `null`. */
  const body = () => ({
    name: values.name.trim(),
    reference: optionalText(values.reference),
    description: optionalText(values.description),
    contractorName: optionalText(values.contractorName),
    consultantName: optionalText(values.consultantName),
    ownerName: optionalText(values.ownerName),
    startDate: values.startDate,
    expectedEndDate: values.expectedEndDate,
    notes: optionalText(values.notes),
  });

  const save = useSaveMutation({
    mutationFn: () =>
      isNew
        ? projectsApi.create({ clientId: values.clientId, ...body() })
        : projectsApi.update(id as string, {
            ...body(),
            ...(values.status ? { status: values.status } : {}),
            ...(values.reason.trim() ? { reason: values.reason.trim() } : {}),
          }),
    successMessage: isNew ? t('projects.created') : t('projects.saved'),
    invalidate: [['projects'], ['project', id], ['client-file', values.clientId]],
    // Create -> the new project's own page; edit -> back to it.
    navigateTo: (saved) => `/projects/${saved.id}`,
  });

  const { fields: serverErrors } = useSaveErrors(save.error);
  const errorFor = (field: string) => {
    const message = localErrors[field] ?? serverErrors[field];
    return message ? { error: message } : {};
  };

  const submit = () => {
    const found = isNew
      ? validationErrors(createProjectSchema, { clientId: values.clientId, ...body() }, t)
      : validationErrors(updateProjectSchema, body(), t);
    setLocalErrors(found);
    if (Object.keys(found).length > 0) return;
    save.mutate();
  };

  if (!isNew && project.isLoading) return <LoadingState label={t('common.loading')} />;
  if (!isNew && project.error) {
    return <ErrorState error={project.error} onRetry={() => void project.refetch()} />;
  }

  const title = isNew ? t('projects.createTitle') : t('projects.editTitle');
  const fieldLabels = {
    clientId: t('projects.client'),
    name: t('projects.name'),
    reference: t('projects.reference'),
    description: t('projects.description'),
    contractorName: t('projects.contractorName'),
    consultantName: t('projects.consultantName'),
    ownerName: t('projects.ownerName'),
    startDate: t('projects.startDate'),
    expectedEndDate: t('projects.expectedEndDate'),
    status: t('projects.statusLabel'),
    reason: t('projects.statusReason'),
    notes: t('projects.notes'),
  };

  return (
    <FormPage
      title={title}
      breadcrumbs={[
        { label: t('nav.projects'), to: '/projects' },
        ...(!isNew && project.data
          ? [{ label: project.data.name, to: `/projects/${id}` }]
          : []),
        { label: title },
      ]}
      onSubmit={submit}
      onCancel={() => navigate(isNew ? '/projects' : `/projects/${id}`)}
      isSubmitting={save.isPending}
      canSubmit={canEdit}
      error={save.error}
      fieldLabels={fieldLabels}
    >
      <FormSection title={t('common.details')}>
        {isNew ? (
          <SelectField
            label={t('projects.client')}
            placeholder={t('projects.selectClient')}
            value={values.clientId}
            onChange={set('clientId')}
            isRequired
            className="sm:col-span-2"
            options={(clients.data?.data ?? []).map((client) => ({
              value: client.id,
              label: client.name || client.clientNumber,
              description: client.clientNumber,
            }))}
            {...errorFor('clientId')}
          />
        ) : null}
        <TextField
          label={t('projects.name')}
          value={values.name}
          onChange={set('name')}
          textDirection="auto"
          isRequired
          isReadOnly={!canEdit}
          {...errorFor('name')}
        />
        <TextField
          label={t('projects.reference')}
          description={t('projects.referenceHint')}
          value={values.reference}
          onChange={set('reference')}
          isReadOnly={!canEdit}
          {...errorFor('reference')}
        />
        <TextAreaField
          label={t('projects.description')}
          value={values.description}
          onChange={set('description')}
          rows={2}
          textDirection="auto"
          isReadOnly={!canEdit}
          className="sm:col-span-2"
          {...errorFor('description')}
        />
      </FormSection>

      <FormSection title={t('projects.parties')} description={t('projects.partiesHint')}>
        <TextField
          label={t('projects.contractorName')}
          value={values.contractorName}
          onChange={set('contractorName')}
          textDirection="auto"
          isReadOnly={!canEdit}
          {...errorFor('contractorName')}
        />
        <TextField
          label={t('projects.ownerName')}
          value={values.ownerName}
          onChange={set('ownerName')}
          textDirection="auto"
          isReadOnly={!canEdit}
          {...errorFor('ownerName')}
        />
        <TextField
          label={t('projects.consultantName')}
          value={values.consultantName}
          onChange={set('consultantName')}
          textDirection="auto"
          isReadOnly={!canEdit}
          {...errorFor('consultantName')}
        />
      </FormSection>

      <FormSection title={t('projects.schedule')}>
        <DateField
          label={t('projects.startDate')}
          value={values.startDate}
          onChange={set('startDate')}
          isReadOnly={!canEdit}
          {...errorFor('startDate')}
        />
        <DateField
          label={t('projects.expectedEndDate')}
          value={values.expectedEndDate}
          onChange={set('expectedEndDate')}
          isReadOnly={!canEdit}
          {...errorFor('expectedEndDate')}
        />
      </FormSection>

      <FormSection title={t('projects.notes')} columns={isNew ? 1 : 2}>
        {!isNew ? (
          <>
            <SelectField
              label={t('projects.statusLabel')}
              value={values.status}
              onChange={set('status')}
              isDisabled={!canEdit}
              options={ALL_PROJECT_STATUSES.map((value) => ({
                value,
                label: t(`projects.status.${value}`),
              }))}
              {...errorFor('status')}
            />
            {/* A status change has to be explainable. Nothing is removed by
                one: a cancelled project keeps everything under it. */}
            <TextField
              label={t('projects.statusReason')}
              value={values.reason}
              onChange={set('reason')}
              textDirection="auto"
              isReadOnly={!canEdit}
              {...errorFor('reason')}
            />
          </>
        ) : null}
        <TextAreaField
          label={t('projects.notes')}
          value={values.notes}
          onChange={set('notes')}
          rows={3}
          textDirection="auto"
          isReadOnly={!canEdit}
          className={isNew ? undefined : 'sm:col-span-2'}
          {...errorFor('notes')}
        />
      </FormSection>
    </FormPage>
  );
}
